import React from "react";
import Select from "react-select";
import "./MultiSelectDropdown.css";

/**
 * Multi-select dropdown using react-select.
 * @param {Object} props
 * @param {Array<{ value: string|number, label: string }>} props.options - Options for the dropdown
 * @param {Array<string|number>} props.value - Selected values (IDs)
 * @param {function(Array<string|number>): void} props.onChange - Called with array of selected IDs
 * @param {string} [props.placeholder] - Placeholder when nothing selected
 * @param {string} [props.label] - Optional label above the select
 * @param {boolean} [props.disabled] - Disable the select
 * @param {boolean} [props.searchable] - Enable search (default true)
 * @param {string} [props.className] - Additional class for the wrapper
 */
function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  label,
  disabled = false,
  searchable = true,
  className = "",
}) {
  // Ensure options is an array
  const safeOptions = Array.isArray(options) ? options : [];

  // Normalize value to array of numbers
  const normalizedValue = Array.isArray(value) 
    ? value.map((v) => (typeof v === "number" ? v : parseInt(v, 10))).filter((n) => !isNaN(n))
    : [];

  // Find selected options based on IDs
  const selectedOptions = safeOptions.filter((opt) => {
    if (!opt || typeof opt !== 'object') return false;
    const optVal = typeof opt.value === "number" ? opt.value : parseInt(opt.value, 10);
    return !isNaN(optVal) && normalizedValue.includes(optVal);
  });

  const handleChange = (selectedItems) => {
    if (!onChange) return;
    // Extract IDs from selected options
    const ids = (selectedItems || []).map((item) => {
      const v = item.value;
      return typeof v === "number" ? v : parseInt(v, 10);
    }).filter((n) => !isNaN(n));
    onChange(ids);
  };

  return (
    <div 
      className={`multi-select-dropdown-wrapper ${className}`}
      style={{ pointerEvents: 'auto', zIndex: 10, position: 'relative' }}
    >
      {label && <label className="multi-select-dropdown-label">{label}</label>}
      <Select
        isMulti
        options={safeOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={disabled || safeOptions.length === 0}
        isSearchable={searchable}
        className="multi-select-dropdown"
        classNamePrefix="multi-select"
        noOptionsMessage={() => "No options available"}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />
    </div>
  );
}

export default MultiSelectDropdown;
